/**
 * ESCAPE COORDINATOR (B10) — one global Escape, many layers. Pure registry;
 * the keyboard listener lives in useGlobalKeys.ts. Tested in escape.test.ts.
 *
 * §H.1: Escape closes the topmost layer, in this order:
 *   menu → layers card → drive → card → sheet (mobile full → half) → search text
 *
 * A layer registers { id, priority, isOpen, close }. On Escape the open layer
 * with the highest priority closes; nothing else happens. Components that handle
 * Escape themselves while focused (the search field, the sort menu, a native
 * <dialog>) call event.preventDefault() and the global handler stands aside.
 */
export interface EscapeLayer {
  id: string;
  priority: number;
  isOpen: () => boolean;
  close: () => void;
}

/** Priorities of the known layers (higher closes first). */
export const ESCAPE_PRIORITY = {
  menu: 60, //    sort menu and other popovers (B7)
  layers: 50, //  layers card (B9)
  drive: 40, //   the drive (built in)
  card: 30, //    the place card (built in)
  sheet: 20, //   mobile sheet at full → half (built in)
  search: 10, //  search text (built in)
} as const;

const layers = new Map<string, EscapeLayer>();

/** Adds (or replaces) a layer. Returns the unregister function. */
export function registerEscapeLayer(layer: EscapeLayer): () => void {
  layers.set(layer.id, layer);
  return () => {
    if (layers.get(layer.id) === layer) layers.delete(layer.id);
  };
}

function safeIsOpen(l: EscapeLayer): boolean {
  try {
    return l.isOpen();
  } catch {
    return false;
  }
}

/** The open layer with the highest priority (ties: the one registered last). */
export function topEscapeLayer(list: Iterable<EscapeLayer> = layers.values()): EscapeLayer | null {
  let best: EscapeLayer | null = null;
  for (const l of list) {
    if (safeIsOpen(l) && (!best || l.priority >= best.priority)) best = l;
  }
  return best;
}

/** Closes the top layer. true when something closed. */
export function closeTopLayer(): boolean {
  const l = topEscapeLayer();
  if (!l) return false;
  l.close();
  return true;
}

/** For tests. */
export function escapeLayerIds(): string[] {
  return [...layers.keys()];
}
